import React from 'react';
import classNames from 'classnames';

import { PAGE_SIZE_SM } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCompactCurrency } from '~libs/formatters';

import { ErrorResponse, Toolbar } from '~components';
import MonthInput from '~components/form-fields/inputs/MonthInput';
import DropdownCheckbox from '~components/form-fields/search/DropdownCheckbox';
import { faCalculator, faDollarSign, faHandHoldingHeart, faMoneyBill } from '~components/icon';
import { TablePagination } from '~components/table-view-v3';
import Statistics, { StatisticConfig } from '~components/visualisation/statistic/Statistics';

import StatementBudgetsTable from './StatementBudgetsTable';
import { useStatementBudgetSummary } from './useStatementBudgetSummary';

const ICON_COLOURS = {
  green: '#008000',
  red: '#ff0000',
  blue: '#1565c0',
  warning: '#faa51a',
} as const;

export const StatementsDashboard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const {
    statementMonth,
    handleMonthChange,
    currentPage,
    totalPages,
    handlePageChange,
    visibleColumns,
    setVisibleColumns,
    columnFilterItems,
    visibleLabels,
    rows,
    summary,
    breakdowns,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
  } = useStatementBudgetSummary(isActive);

  const statistics: StatisticConfig[] | null =
    summary && !isLoading
      ? [
          {
            title: 'Total Opening Balance',
            type: 'value',
            value: formatCompactCurrency(summary.total_opening_balance),
            icon: faMoneyBill,
            iconColor: ICON_COLOURS.green,
            breakdown: breakdowns.opening,
            dataCy: 'statements-dashboard-total-opening',
          },
          {
            title: 'Total Spent',
            type: 'value',
            value: formatCompactCurrency(summary.total_spent),
            icon: faDollarSign,
            iconColor: ICON_COLOURS.red,
            breakdown: breakdowns.spent,
            dataCy: 'statements-dashboard-total-spent',
          },
          {
            title: 'Total Closing Balance',
            type: 'value',
            value: formatCompactCurrency(summary.total_closing_balance),
            icon: faCalculator,
            iconColor: ICON_COLOURS.blue,
            breakdown: breakdowns.closing,
            dataCy: 'statements-dashboard-total-closing',
          },
          {
            title: 'Total Co-Contributions',
            type: 'value',
            value: formatCompactCurrency(summary.total_co_contributions),
            icon: faHandHoldingHeart,
            iconColor: ICON_COLOURS.warning,
            dataCy: 'statements-dashboard-total-co-contributions',
          },
        ]
      : null;

  return (
    <div
      className={classNames('statements-dashboard', { fetching: isFetching })}
      data-cy="statements-dashboard"
      aria-busy={isFetching}
    >
      <Toolbar
        id="statements-dashboard"
        leftCluster={
          <MonthInput
            id="statement-month"
            name="statement_month"
            type="month"
            value={statementMonth}
            placeholder="Statement Month"
            onChange={handleMonthChange}
            dataCy="statements-dashboard"
          />
        }
        rightCluster={
          columnFilterItems.length > 0 ? (
            <DropdownCheckbox
              caption="Filter"
              items={columnFilterItems}
              values={visibleColumns}
              onClick={setVisibleColumns}
              dataCy="statements-dashboard-column-filter"
            />
          ) : undefined
        }
      />
      {isError && (
        <ErrorResponse
          message={`Failed to load statement budget summary - ${getErrorMessage(error)}`}
          dataCy="statements-dashboard-error"
        />
      )}
      {!isError && (
        <>
          {statistics && (
            <div className="statements-summary">
              <Statistics statistics={statistics} />
            </div>
          )}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={PAGE_SIZE_SM}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            isLoading={isFetching}
            showPageSizeOptions={false}
          />
          <StatementBudgetsTable rows={rows} isLoading={isLoading} visibleFundingTypes={visibleLabels} />
        </>
      )}
    </div>
  );
};
